import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  Alert,
  AlertTitle,
  Divider,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Info as InfoIcon,
  Calculate as CalculateIcon,
} from '@mui/icons-material';
import { formatMoney } from 'lib/functions/utils';
import { Document, Warranty } from 'lib/types/models';
import { calculateRiskPremium } from 'lib/functions/riskPremiumCalculator';

interface PaymentDocumentProps {
  document?: Document;
  warranty: Warranty;
}

export default function PaymentDocument({
  document,
  warranty,
}: PaymentDocumentProps) {
  const doc = document;

  const isAmountInCLP =
    warranty.fixedAmount || warranty.quotedCurrency === 'CLP';
  const effectiveConversionRate = isAmountInCLP
    ? 1
    : warranty.conversionRate || 1;

  const costs = calculateRiskPremium({
    amount: warranty.fixedAmount || warranty.quotedAmount,
    days:
      (new Date(warranty.endDate).getTime() -
        new Date(warranty.startDate).getTime()) /
      (1000 * 60 * 60 * 24),
    conversionRate: effectiveConversionRate,
    type: warranty.type,
    rates: {
      variableRate: warranty.operationCostVariable,
      fixedRate: warranty.operationCostFixed,
      variableDiscount: warranty.discountRateVariable,
      fixedDiscount: warranty.discountRateFixed,
    },
  });

  return (
    <Paper sx={{ p: 1.5, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Box
          sx={{
            backgroundColor: 'primary.main',
            borderRadius: '50%',
            p: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}
        >
          <DescriptionIcon sx={{ fontSize: '1rem' }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: '1rem',
              lineHeight: 1.1,
            }}
          >
            Documento de Pago
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <Chip
              label={'Prima de Garantía'}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: '18px', fontSize: '0.65rem' }}
            />
            <Chip
              label={doc?.paidAt ? 'Pagado' : 'Pendiente'}
              size="small"
              color={doc?.paidAt ? 'success' : 'warning'}
              sx={{ height: '18px', fontSize: '0.65rem' }}
            />
          </Stack>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Stack
            direction="row"
            alignItems="start"
            justifyContent="flex-end"
            spacing={0.75}
          >
            <Typography variant="h4" color="primary">
              {formatMoney(doc?.amount || 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              CLP
            </Typography>
          </Stack>
        </Box>
      </Stack>

      <Box
        sx={{
          mb: 2,
          p: 1.5,
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
          borderRadius: 1,
        }}
      >
        <Stack spacing={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="caption" color="text.secondary">
              Folio:
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              {doc?.folio}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="caption" color="text.secondary">
              Fecha de Emisión:
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              {doc?.issuedDate
                ? new Date(doc.issuedDate).toLocaleDateString('es-CL')
                : '-'}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="caption" color="text.secondary">
              Empresa:
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              {doc?.businessIdentifier}
            </Typography>
          </Stack>
        </Stack>
      </Box>

      {/* Cost Calculation Details */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <CalculateIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
          <Typography variant="subtitle2">Detalle del Cálculo</Typography>
        </Stack>
        <Box
          sx={{
            p: 1.5,
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            borderRadius: 1,
          }}
        >
          <Stack spacing={1}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="caption" color="text.secondary">
                Costo Variable:
              </Typography>
              <Typography variant="caption" fontWeight={600}>
                {formatMoney(costs?.variableComponent || 0)} CLP
              </Typography>
            </Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="caption" color="text.secondary">
                Costo Fijo:
              </Typography>
              <Typography variant="caption" fontWeight={600}>
                {formatMoney(costs?.fixedComponent || 0)} CLP
              </Typography>
            </Stack>
            <Divider sx={{ my: 0.5 }} />
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="caption" fontWeight={600}>
                Total:
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color="primary.main"
              >
                {formatMoney(costs?.totalPremium || 0)} CLP
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Box>

      <Alert
        severity="info"
        icon={<InfoIcon sx={{ fontSize: '0.9rem' }} />}
        sx={{
          borderRadius: 0.5,
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          border: '1px solid rgba(25, 118, 210, 0.2)',
          py: 0.75,
          mt: 2,
          '& .MuiAlert-message': { py: 0.25 },
        }}
      >
        <AlertTitle sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
          Información Importante
        </AlertTitle>
        <Typography
          variant="body2"
          sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}
        >
          {doc?.paidAt
            ? 'El pago ha sido registrado por el cliente. Por favor, verifique el comprobante de pago antes de proceder con la generación del certificado.'
            : 'El certificado de garantía no será emitido hasta que se realice y verifique el pago correspondiente.'}
        </Typography>
      </Alert>
    </Paper>
  );
}
